import { useEffect, useState, useCallback } from "react";
import api from "../api/client";

const PLANES = ["gratis", "emprendedor", "empresa"];
const PLAN_COLOR = {
  gratis:      { bg: "#f3f4f6", text: "#374151", dot: "#9ca3af" },
  emprendedor: { bg: "#eef2ff", text: "#4338ca", dot: "#6366f1" },
  empresa:     { bg: "#faf5ff", text: "#7c3aed", dot: "#a855f7" },
  basico:      { bg: "#eef2ff", text: "#4338ca", dot: "#6366f1" },
  pro:         { bg: "#faf5ff", text: "#7c3aed", dot: "#a855f7" },
};

function StatCard({ label, valor, sub, color = "#6366f1", icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
          <p className="text-2xl font-bold" style={{ color }}>{valor}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        {icon && <span className="text-2xl opacity-60">{icon}</span>}
      </div>
    </div>
  );
}

function PlanBadge({ plan }) {
  const c = PLAN_COLOR[plan] || PLAN_COLOR.gratis;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize"
      style={{ background: c.bg, color: c.text }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.dot }} />
      {plan}
    </span>
  );
}

function ModalSuscripcion({ tenant, onClose, onSaved }) {
  const sus = tenant.suscripcion;
  const [form, setForm] = useState({
    plan: sus?.plan || "gratis",
    monto: sus?.monto ?? "",
    activa: sus?.activa ?? true,
    fecha_fin: sus?.fecha_fin ? sus.fecha_fin.substring(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const guardar = async () => {
    setSaving(true); setError("");
    try {
      const payload = {
        plan: form.plan,
        activa: form.activa,
        monto: form.monto !== "" ? parseFloat(form.monto) : null,
        fecha_fin: form.fecha_fin ? new Date(form.fecha_fin + "T23:59:59").toISOString() : null,
      };
      const { data } = await api.put(`/api/accounts/${tenant.id}/plan`, payload);
      onSaved(data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900">Editar suscripción</h3>
            <p className="text-xs text-gray-400 mt-0.5">{tenant.nombre_tienda}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 text-lg">×</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plan</label>
            <div className="grid grid-cols-3 gap-2">
              {PLANES.map((p) => {
                const c = PLAN_COLOR[p];
                const active = form.plan === p;
                return (
                  <button key={p} type="button" onClick={() => setForm(f => ({ ...f, plan: p }))}
                    className="py-2.5 rounded-xl text-sm font-semibold capitalize transition-all border-2"
                    style={{
                      borderColor: active ? c.dot : "#e5e7eb",
                      background: active ? c.bg : "#fff",
                      color: active ? c.text : "#6b7280",
                    }}>
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Monto mensual $</label>
              <input type="number" min="0" step="0.01" value={form.monto}
                onChange={(e) => setForm(f => ({ ...f, monto: e.target.value }))}
                placeholder="0.00"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha vencimiento</label>
              <input type="date" value={form.fecha_fin}
                onChange={(e) => setForm(f => ({ ...f, fecha_fin: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm font-medium text-gray-700">Suscripción activa</p>
              <p className="text-xs text-gray-400">Desactivar bloquea el acceso al panel</p>
            </div>
            <button type="button" onClick={() => setForm(f => ({ ...f, activa: !f.activa }))}
              className={`w-12 h-6 rounded-full transition-colors relative ${form.activa ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.activa ? "left-6" : "left-0.5"}`} />
            </button>
          </div>
        </div>

        <div className="flex gap-3 px-6 pb-5">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={guardar} disabled={saving}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl text-sm font-medium disabled:opacity-50 transition-colors">
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilaTenant({ t, onEdit, onToggle }) {
  const fechaReg = t.fecha_creacion
    ? new Date(t.fecha_creacion).toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const vence = t.suscripcion?.fecha_fin
    ? new Date(t.suscripcion.fecha_fin).toLocaleDateString("es-VE", { day: "2-digit", month: "short", year: "numeric" })
    : "—";
  const vencido = t.suscripcion?.fecha_fin && new Date(t.suscripcion.fecha_fin) < new Date();

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-5 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
            {t.nombre_tienda.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-800 text-sm leading-tight">{t.nombre_tienda}</p>
            <p className="text-xs text-gray-400">{t.subdominio} · {t.pais}</p>
          </div>
        </div>
      </td>
      <td className="px-5 py-3 text-xs text-gray-500 hidden lg:table-cell">{t.email_owner}</td>
      <td className="px-5 py-3">
        <div className="flex flex-col gap-1">
          <PlanBadge plan={t.suscripcion?.plan || "gratis"} />
          {t.suscripcion?.monto > 0 && (
            <span className="text-xs text-green-700 font-semibold">${t.suscripcion.monto}/mes</span>
          )}
        </div>
      </td>
      <td className="px-5 py-3 hidden md:table-cell">
        <span className={`text-xs font-medium ${vencido ? "text-red-600" : "text-gray-500"}`}>
          {vence}{vencido && <span className="ml-1 text-red-500">(vencido)</span>}
        </span>
      </td>
      <td className="px-5 py-3 hidden xl:table-cell">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span title="Productos">📦 {t.stats.productos}</span>
          <span title="Visitas 30d">👁 {t.stats.visitas_30d}</span>
          <span title="Pedidos">🛒 {t.stats.pedidos}</span>
          <span title="Clientes">👥 {t.stats.clientes}</span>
        </div>
      </td>
      <td className="px-5 py-3 text-right hidden lg:table-cell">
        <span className="text-sm font-bold text-gray-800">${t.stats.revenue.toFixed(2)}</span>
      </td>
      <td className="px-5 py-3 text-center">
        <button onClick={() => onToggle(t)}
          className={`text-xs font-semibold px-2.5 py-1 rounded-full transition-colors ${t.activo ? "bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700" : "bg-red-100 text-red-700 hover:bg-green-100 hover:text-green-700"}`}>
          {t.activo ? "Activo" : "Inactivo"}
        </button>
      </td>
      <td className="px-5 py-3 text-xs text-gray-400 hidden md:table-cell">{fechaReg}</td>
      <td className="px-5 py-3 text-center">
        <button onClick={() => onEdit(t)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors">
          Editar plan
        </button>
      </td>
    </tr>
  );
}

export default function Dashboard({ usuario, onLogout }) {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ total: 0, items: [] });
  const [page, setPage] = useState(1);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaActiva, setBusquedaActiva] = useState("");
  const [planFiltro, setPlanFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const PAGE_SIZE = 20;

  const cargarStats = () => {
    api.get("/api/overview").then((r) => setStats(r.data)).catch(() => {});
  };

  const cargar = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
    if (busquedaActiva) params.append("busqueda", busquedaActiva);
    if (planFiltro) params.append("plan", planFiltro);
    api.get(`/api/accounts?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [busquedaActiva, planFiltro, page]);

  useEffect(() => { cargarStats(); }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const buscar = () => { setBusquedaActiva(busqueda); setPage(1); };  // cargar() se dispara por el useEffect al cambiar busquedaActiva o page

  const handleSaved = (tenantActualizado) => {
    setData((d) => ({ ...d, items: d.items.map((t) => t.id === tenantActualizado.id ? tenantActualizado : t) }));
    cargarStats();
  };

  const toggleTenant = async (t) => {
    if (!window.confirm(`¿${t.activo ? "Desactivar" : "Activar"} la empresa "${t.nombre_tienda}"?`)) return;
    try {
      await api.put(`/api/accounts/${t.id}/status`);
      setData((d) => ({ ...d, items: d.items.map((x) => x.id === t.id ? { ...x, activo: !x.activo } : x) }));
    } catch { alert("No se pudo cambiar el estado"); }
  };

  const handleLogout = async () => {
    try { await api.delete("/api/auth"); } catch (_) {}
    onLogout();
  };

  const totalPages = Math.ceil(data.total / PAGE_SIZE);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Topbar */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-lg font-black">
            <span className="text-indigo-600">Tu</span>
            <span className="text-gray-900">Catalogo</span>
            <span className="text-orange-500">.net</span>
          </div>
          <span className="bg-red-100 text-red-700 text-xs font-bold px-2.5 py-0.5 rounded-full tracking-wider uppercase">
            Admin Sistema
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 hidden sm:block">{usuario?.nombre} · {usuario?.email}</span>
          <button onClick={handleLogout}
            className="text-sm text-gray-500 hover:text-red-600 font-medium transition-colors">
            Cerrar sesión
          </button>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Panel de Administración</h2>
          <p className="text-gray-500 text-sm mt-1">Vista global de todas las empresas registradas en la plataforma</p>
        </div>

        {/* Stats globales */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Empresas registradas" valor={stats.total_tenants} sub={`${stats.tenants_activos} activas`} color="#6366f1" icon="🏢" />
            <StatCard label="Visitas últimos 30 días" valor={stats.visitas_30d.toLocaleString()} color="#8b5cf6" icon="👁" />
            <StatCard label="Revenue total plataforma" valor={`$${stats.revenue_total.toFixed(2)}`} color="#10b981" icon="💰" />
            <StatCard label="Clientes registrados" valor={stats.total_clientes.toLocaleString()} color="#f59e0b" icon="👥" />
          </div>
        )}

        {/* Filtros por plan */}
        {stats && (
          <div className="grid grid-cols-3 gap-3">
            {PLANES.map((p) => {
              const c = PLAN_COLOR[p];
              const n = stats.por_plan[p] || 0;
              return (
                <button key={p} onClick={() => { setPlanFiltro(planFiltro === p ? "" : p); setPage(1); }}
                  className="bg-white rounded-xl border-2 transition-all p-4 text-left"
                  style={{ borderColor: planFiltro === p ? c.dot : "#e5e7eb" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.dot }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: c.text }}>{p}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{n}</p>
                  <p className="text-xs text-gray-400">empresa{n !== 1 ? "s" : ""}</p>
                </button>
              );
            })}
          </div>
        )}

        {/* Buscador */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex gap-3">
            <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && buscar()}
              placeholder="Buscar por nombre, subdominio o email..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            <button onClick={buscar}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
              Buscar
            </button>
            {(busquedaActiva || planFiltro) && (
              <button onClick={() => { setBusqueda(""); setBusquedaActiva(""); setPlanFiltro(""); setPage(1); }}
                className="border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <div className="text-5xl mb-3">🏢</div>
              <p className="font-medium">Sin resultados</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-400 uppercase text-xs">
                    <tr>
                      <th className="px-5 py-3 text-left">Empresa</th>
                      <th className="px-5 py-3 text-left hidden lg:table-cell">Email</th>
                      <th className="px-5 py-3 text-left">Plan</th>
                      <th className="px-5 py-3 text-left hidden md:table-cell">Vence</th>
                      <th className="px-5 py-3 text-left hidden xl:table-cell">Uso</th>
                      <th className="px-5 py-3 text-right hidden lg:table-cell">Revenue</th>
                      <th className="px-5 py-3 text-center">Estado</th>
                      <th className="px-5 py-3 text-left hidden md:table-cell">Registro</th>
                      <th className="px-5 py-3 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.items.map((t) => (
                      <FilaTenant key={t.id} t={t} onEdit={setEditando} onToggle={toggleTenant} />
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-gray-500">
                  <span>Página {page} de {totalPages} — {data.total} empresas</span>
                  <div className="flex gap-2">
                    <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Anterior</button>
                    <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Siguiente</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {editando && (
        <ModalSuscripcion tenant={editando} onClose={() => setEditando(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}
